import { useResourceBudgetSettings } from '@/hooks/use-resource-budget-settings';
import { ResourceBudgetView } from './resource-budget-view';
import { MonthlyResourceBudgetView } from './monthly-resource-budget-view';
import type { ResourceBudget, BudgetRevision, CostCentreRequestGroup } from '@/types/budget.types';
import type { Employee, ResourceAllocation } from '@/types';
import type { AssignmentSelection } from '@/types/assignment.types';

interface ResourceBudgetWrapperProps {
  projectId: number;
  data: ResourceBudget;
  onEdit?: () => void;
  onExport?: () => void;
  onHistory?: () => void;
  availablePeople?: Employee[];
  existingAllocations?: ResourceAllocation[];
  projectStartDate?: string;
  projectEndDate?: string;
  onAssign?: (role: string, selections: AssignmentSelection[]) => void;
  onRaiseRequest?: (roleName?: string) => void;
  /** Cost Centre mode only — moves the request action onto the cost-centre parent rows. */
  isCostCentreMode?: boolean;
  /** Cost Centre mode only — cost centre code to display name. */
  costCentreNames?: Record<string, string>;
  /** Cost Centre mode only — raises one request per role under the cost centre. */
  onRaiseCostCentreRequest?: (group: CostCentreRequestGroup) => void;
  /** Set of role names that have a pending request this session */
  pendingRequestRoles?: Set<string>;
  /** When set, renders from historical snapshot in read-only mode */
  historicalRevision?: BudgetRevision;
  onCompareWithCurrent?: () => void;
  onRestoreAsRevision?: () => void;
  onReturnToCurrent?: () => void;
  onOpenWhatIf?: () => void;
}

/**
 * Wrapper component that conditionally renders the appropriate resource budget view
 * based on the global budgeting type setting.
 * 
 * Note: When budgetType = 'none', the entire Resource Budget tab is hidden at the 
 * BudgetTabContainer level, so this component will never render in that case.
 * 
 * - budgetType = 'simple': Shows the standard resource budget view
 * - budgetType = 'monthly': Shows the monthly resource budget view
 */
export function ResourceBudgetWrapper({ projectId, data, onEdit, onExport, onHistory, availablePeople, existingAllocations, projectStartDate, projectEndDate, onAssign, onRaiseRequest, isCostCentreMode, costCentreNames, onRaiseCostCentreRequest, pendingRequestRoles, historicalRevision, onCompareWithCurrent, onRestoreAsRevision, onReturnToCurrent, onOpenWhatIf }: ResourceBudgetWrapperProps) {
  const { settings } = useResourceBudgetSettings();

  // If budget type is 'monthly', render the monthly view
  if (settings.budgetType === 'monthly') {
    return (
      <MonthlyResourceBudgetView
        projectId={projectId}
        data={data}
        settings={settings}
        onEdit={onEdit}
        onExport={onExport}
        onHistory={onHistory}
        onRaiseRequest={onRaiseRequest}
        historicalRevision={historicalRevision}
      />
    );
  }

  // Default: render the simple budget view (for 'simple' or any other value)
  return (
    <ResourceBudgetView
      projectId={projectId}
      data={data}
      onEdit={onEdit}
      onExport={onExport}
      onHistory={onHistory}
      availablePeople={availablePeople}
      existingAllocations={existingAllocations}
      projectStartDate={projectStartDate}
      projectEndDate={projectEndDate}
      onAssign={onAssign}
      onRaiseRequest={onRaiseRequest}
      isCostCentreMode={isCostCentreMode}
      costCentreNames={costCentreNames}
      onRaiseCostCentreRequest={onRaiseCostCentreRequest}
      pendingRequestRoles={pendingRequestRoles}
      historicalRevision={historicalRevision}
      onCompareWithCurrent={onCompareWithCurrent}
      onRestoreAsRevision={onRestoreAsRevision}
      onReturnToCurrent={onReturnToCurrent}
      onOpenWhatIf={onOpenWhatIf}
    />
  );
}
